import autolinker from "autolinker";
import {
  CompositeDecorator,
  EditorState,
  getVisibleSelectionRect,
  Modifier,
  RichUtils,
  SelectionState,
} from "draft-js";
import React from "react";
// Useful functions for working with draft-js editorState objects.

export const Link = (props) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a href={url} title={url}>
      {props.children}
    </a>
  );
};

// Standard function for inflating an editorState object with autolinking
// from a database-serialized response
export const editorStateFromDefault = (defaultValue) => {
  const decorator = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: Link,
    },
  ]);

  const editorState = defaultValue
    ? EditorState.createWithContent(defaultValue, decorator)
    : EditorState.createEmpty(decorator);

  // initial load focus issue is being caused by a disagreement between Draft and
  // our atomic block code on when the editor is losing focus. By giving it initial
  // focus, we can fix one of the more obvious issues that KPR was having.
  const withFocus = EditorState.forceSelection(editorState, editorState.getSelection().merge({ hasFocus: true }));
  return withFocus;
};

// find link *entities* that the user has manually specified through the
// editor UI
export const findLinkEntities = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK";
  }, callback);
};

// find plain-text hyperlinks in the text
export const findPlainTextLinks = (contentBlock, callback, contentState) => {
  const matches = autolinker.parse(contentBlock.get("text"), {
    email: false,
    urls: { schemeMatches: true, wwwMatches: false, tldMatches: false },
    phone: false,
  });
  // we don't need to filter out manual link entities *as long as* the
  // findLinkEntities strategy comes first when you spin up a CompositeDecorator
  matches.forEach((m) => {
    callback(m.offset, m.offset + m.matchedText.length);
  });
};

// find plain-text emails in the text
export const findPlainTextEmails = (contentBlock, callback, contentState) => {
  const matches = autolinker.parse(contentBlock.get("text"), {
    email: true,
    urls: false,
    phone: false,
  });
  // we don't need to filter out manual link entities *as long as* the
  // findLinkEntities strategy comes first when you spin up a CompositeDecorator
  matches.forEach((m) => {
    callback(m.offset, m.offset + m.matchedText.length);
  });
};

// pulled directly from https://github.com/facebook/draft-js/blob/master/examples/draft-0-10-0/link/link.html
export const addLinkToEditorState = (editorState, urlValue) => {
  const contentState = editorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", {
    url: urlValue,
  });
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const newEditorState = EditorState.set(editorState, {
    currentContent: contentStateWithEntity,
  });
  return RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey);
};

// returns true if the start of the selection is a link. Works for collapsed and
// non-collapsed selections.
export const selectionIsLink = (editorState) => {
  const entity = getEntityKeyAtCursor(editorState);
  const contentState = editorState.getCurrentContent();
  return entity && contentState.getEntity(entity).getType() === "LINK";
};

export const getSelectedLinkUrl = (editorState) => {
  const entity = getEntityKeyAtCursor(editorState, "leading", false);
  const contentState = editorState.getCurrentContent();
  return entity && contentState.getEntity(entity).data?.url;
};

export const getEntityKeyAtCursor = (editorState, cursor = "leading", useOffset = true) => {
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();
  let entity;
  let offsetAdjust;
  useOffset ? (offsetAdjust = -1) : (offsetAdjust = 0);
  if (cursor === "leading") {
    const leadingBlock = content.getBlockMap().get(selection.getStartKey());
    let offset = selection.getStartOffset() + offsetAdjust; // this allows entity check if cursor is at the end of a word
    if (offset < 0) {
      offset = 0;
    }
    entity = leadingBlock.getEntityAt(offset);
  } else if (cursor === "trailing") {
    const trailingBlock = content.getBlockMap().get(selection.getEndKey());
    entity = trailingBlock.getEntityAt(selection.getEndOffset());
  } else {
    throw Error(`unrecognized option '${cursor}' `);
  }
  return entity;
};

export const getEntityAtCursor = (editorState, cursor = "leading") => {
  const entity = getEntityKeyAtCursor(editorState, cursor);
  const contentState = editorState.getCurrentContent();
  return entity && contentState.getEntity(entity);
};

// remove link at cursor
export const removeLink = (editorState) => {
  const contentState = editorState.getCurrentContent();
  const entityKey = getEntityKeyAtCursor(editorState);
  if (!entityKey) {
    return editorState;
  }
  const entity = contentState.getEntity(entityKey);
  if (entity && entity.getType() === "LINK") {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const currentBlock = content.getBlockForKey(selection.getStartKey());
    // this is super awkward to do because of the way Entities are this weird
    // global thing that's not associated with contentState except after
    // serialization.
    // Hopefully https://github.com/facebook/draft-js/pull/376 will fix.
    let newEditorState = editorState;
    const linkFilter = (character) => character.getEntity() === entityKey;
    const removeEntity = (start, end) => {
      const entityRange = SelectionState.createEmpty(currentBlock.getKey()).merge({
        anchorKey: currentBlock.getKey(),
        anchorOffset: start,
        focusKey: currentBlock.getKey(),
        focusOffset: end,
      });
      const newContent = Modifier.applyEntity(content, entityRange, null).merge({ selectionAfter: selection });
      newEditorState = EditorState.push(editorState, newContent, "apply-entity");
    };
    currentBlock.findEntityRanges(linkFilter, removeEntity);
    return newEditorState;
  } else {
    return editorState;
  }
};

export const snapSelectionToEntity = (editorState, forceSelection = false) => {
  const contentState = editorState.getCurrentContent();
  const entityKey = getEntityKeyAtCursor(editorState);
  if (!entityKey) {
    return editorState;
  }
  const entity = contentState.getEntity(entityKey);
  if (entity) {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const currentBlock = content.getBlockForKey(selection.getStartKey());
    let newEditorState = editorState;

    const filterEntities = (character) => character.getEntity() === entityKey;
    const setSelection = (start, end) => {
      const entityRange = SelectionState.createEmpty(currentBlock.getKey()).merge({
        anchorKey: currentBlock.getKey(),
        anchorOffset: start,
        focusKey: currentBlock.getKey(),
        focusOffset: end,
      });
      if (forceSelection) {
        newEditorState = EditorState.forceSelection(editorState, entityRange);
      } else {
        newEditorState = EditorState.acceptSelection(editorState, entityRange);
      }
    };
    currentBlock.findEntityRanges(filterEntities, setSelection);
    return newEditorState;
  } else {
    return editorState;
  }
};

export const getVisibleSelectionRectFixed = (window) => {
  const selection = getVisibleSelectionRect(window);

  // When selection the first word of the wrapped line, chrome will return two rects
  // And the first rect has 0 width
  if (selection) {
    const selectionRects = window.getSelection().getRangeAt(0).getClientRects();
    if (selectionRects.length === 2 && selectionRects[0].width === 0) {
      return selectionRects[1];
    }
  }
  return selection;
};

export const findUrlsToLink = (contentBlock, callback) => {
  // original version: http://daringfireball.net/2010/07/improved_regex_for_matching_urls
  // using reformatted/js-friendly version from SO: http://stackoverflow.com/questions/6927719/url-regex-does-not-work-in-javascript
  const regexp =
    /\b((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’]))/gi; // eslint-disable-line

  const text = contentBlock.getText();
  let matchArr, start;
  while ((matchArr = regexp.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

export const Autolink = (props) => {
  const { decoratedText: url } = props;
  return (
    <a href={url} target="_blank" rel="noopener">
      {url}
    </a>
  );
};

export const linkDecorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
  {
    strategy: findUrlsToLink,
    component: Autolink,
  },
]);
